// The Condition tab's rating lines for one area (#468, #469; DD 2026-10-22).
// Which rating record a line edits is decided by its id, worked out from what
// it rates (conditionRecordId), so two iPads rating the same thing make one record:
//  - Measured surfaces with no name of their own are rated with their type: one
//    line per type per area, its rating's id from the visit area and the type
//    (as since #457, so ratings made before this keep their records).
//  - A surface with its own name (surfaceLabel, #412) has a line of its own, its
//    rating's id from the visit area and the SURFACE's id, so a rename keeps it.
//  - A rating nothing measured matches any more is never dropped: it keeps a line
//    under the name it was saved with, marked as not on the Measure tab. That is
//    a type's rating once the last unnamed surface of the type is named (the named
//    surface gets a line of its own), a named surface's rating once the surface is
//    removed, and a rating made before "+ Add surface" added a measured surface.
//  - A line removed from the report (#509) keeps its rating record, made inactive
//    for the WHS history, never deleted. It is kept here, marked removed: the
//    screen hides it, and "+ Add surface" offers it back (and knows its type
//    already has a line, so never adds a second surface of it).
// Pure.
import { conditionRecordId } from '@higgins/condition-library';
import { parseSurfaces, surfaceLabel, unitChoosable, type MeasuredSurface, type SurfaceType } from '@higgins/m2q-pricing';
import type { DeviceRecord, Fields } from './device-db';

export interface RatingLine {
  /** The rating record's id: the line's key, and the record a first rating makes. */
  id: string;
  typeId: string;
  name: string;
  /** A named surface's type ("Other"): what its defects are chosen by (hig_basesurface); null when the name is the type's. */
  baseSurface: string | null;
  /** What the id is worked out from with the visit area: the surface's id (named) or its type's. */
  ratedBy: string;
  /** False: rated, but nothing on the Measure tab answers to it now. */
  measured: boolean;
  /** Taken off the condition report: its rating record is inactive. Not shown. */
  removed: boolean;
  observation: DeviceRecord | undefined;
}

/** A rating record's state when its line is taken off the report (statecode; the API's Active / Inactive). */
export const REMOVED_STATE = 'Inactive';
/** Its state once brought back. */
export const ACTIVE_STATE = 'Active';

export const isRemovedRating = (observation: DeviceRecord | undefined): boolean => observation?.fields.statecode === REMOVED_STATE;

/**
 * The types "+ Add surface" offers. A type already rated as a type, measured or
 * removed, would only join that line, so isn't offered: a removed one comes back
 * through its "(removed)" choice. An "Other" is always a surface of its own.
 */
export const addableTypes = (types: readonly SurfaceType[], lines: readonly RatingLine[]): SurfaceType[] =>
  types.filter((t) => unitChoosable(t) || !lines.some((l) => (l.measured || l.removed) && l.ratedBy === t.id));

/** A rating's new name when its surface was renamed since it was saved: the line's name rides on its next change. */
export const nameChange = (observation: DeviceRecord, line: Pick<RatingLine, 'name'>): Fields =>
  observation.fields.hig_name === line.name ? {} : { hig_name: line.name };

/** A rating's record id: the visit area's, and the surface's id (a named surface) or its type's. */
export const ratingId = (visitAreaId: string, ratedBy: string): string => conditionRecordId('observation', visitAreaId, ratedBy);

/** The area's measured surfaces; null when they are in a format this app version can't read. */
export function measuredSurfaces(stored: Fields[string]): MeasuredSurface[] | null {
  try {
    return parseSurfaces(typeof stored === 'string' ? stored : null).surfaces;
  } catch {
    return null;
  }
}

/** One line per type for unnamed surfaces, one per named surface, in measured order. */
function measuredLines(surfaces: readonly MeasuredSurface[], visitAreaId: string): Omit<RatingLine, 'observation' | 'removed'>[] {
  const lines = new Map<string, Omit<RatingLine, 'observation' | 'removed'>>();
  for (const s of surfaces) {
    const ratedBy = s.name ? s.id : s.typeId;
    const id = ratingId(visitAreaId, ratedBy);
    if (!lines.has(id)) lines.set(id, { id, typeId: s.typeId, name: surfaceLabel(s), baseSurface: s.name ? s.typeName : null, ratedBy, measured: true });
  }
  return [...lines.values()];
}

/**
 * The area's lines: what is measured, then every rating that matches none of it.
 * `surfaces` null (unreadable) shows the ratings alone, none marked as unmeasured.
 * `observations`: the visit area's rating records, removed ones included.
 */
export function ratingLines(surfaces: readonly MeasuredSurface[] | null, visitAreaId: string, observations: readonly DeviceRecord[]): RatingLine[] {
  const byId = new Map(observations.map((o) => [o.id.toLowerCase(), o]));
  const lines: RatingLine[] = measuredLines(surfaces ?? [], visitAreaId).map((l) => {
    const observation = byId.get(l.id);
    return { ...l, removed: isRemovedRating(observation), observation };
  });
  const shown = new Set(lines.map((l) => l.id));
  for (const o of observations) {
    if (shown.has(o.id.toLowerCase())) continue;
    const typeId = typeof o.fields.hig_surfacetypeid === 'string' ? o.fields.hig_surfacetypeid : '';
    const baseSurface = typeof o.fields.hig_basesurface === 'string' ? o.fields.hig_basesurface : null;
    lines.push({ id: o.id, typeId, name: String(o.fields.hig_name ?? ''), baseSurface, ratedBy: typeId, measured: surfaces === null, removed: isRemovedRating(o), observation: o });
  }
  return lines;
}
